import React, { useState, useEffect, useMemo } from 'react';
import { GitBranch, ChevronRight, ChevronDown, Search, X, Loader2, Filter } from 'lucide-react';
import { getRoleBadgeClass, getRoleLabel, getStatusBadgeClass, getStatusLabel } from './shared';

const ROLE_OPTIONS = [['', 'Alle Rollen'], ['admin', 'Admin-Botschafter'], ['coach', 'Botschafter'], ['sponsor', 'Förderer'], ['user', 'Freigeist']];
const STATUS_OPTIONS = [['', 'Alle Status'], ['active', 'Aktiv'], ['pending', 'Ausstehend'], ['email_pending', 'E-Mail-Bestätigung ausstehend'], ['banned', 'Gesperrt'], ['rejected', 'Abgelehnt']];

function nodeMatches(node, { term, role, status, project }, projects) {
  if (role && node.role !== role) return false;
  if (status && (node.status || 'active') !== status) return false;
  const ids = node.project_ids || {};
  if (project === '__none__' && Object.values(ids).some(Boolean)) return false;
  if (project && project !== '__none__' && !ids[project]) return false;
  if (!term) return true;
  const t = term.toLowerCase();
  if ((node.name || '').toLowerCase().includes(t) || (node.username || '').toLowerCase().includes(t) || getRoleLabel(node.role).toLowerCase().includes(t)) return true;
  return Object.entries(ids).some(([pid, val]) => {
    const pname = projects.find(p => p.id === pid)?.name || '';
    return !!val && (pname.toLowerCase().includes(t) || String(val).toLowerCase().includes(t));
  });
}

function annotate(node, filters, projects) {
  const children = (node.children || []).map(c => annotate(c, filters, projects)).filter(Boolean);
  const self = nodeMatches(node, filters, projects);
  if (!self && children.length === 0) return null;
  return { ...node, children, _self: self };
}

function countNodes(node) {
  return (node._self ? 1 : 0) + (node.children || []).reduce((s, c) => s + countNodes(c), 0);
}

function TreeNode({ node, depth = 0, filtering, projects }) {
  const [open, setOpen] = useState(depth < 2);
  useEffect(() => { if (filtering) setOpen(true); }, [filtering]);
  const hasChildren = node.children && node.children.length > 0;
  const ids = node.project_ids || {};
  const projectEntries = projects.filter(p => ids[p.id]);
  return (
    <div className="tree-node" style={{ marginLeft: depth * 20 }} data-testid={`tree-node-${node.id}`}>
      <div className={`tree-node-header ${filtering && node._self ? 'tree-search-match' : ''} ${filtering && !node._self ? 'tree-node-dim' : ''}`} onClick={() => hasChildren && setOpen(!open)}>
        {hasChildren ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span style={{ width: 14 }} />}
        <span className="tree-name">{node.name}</span>
        <span className={`role-badge ${getRoleBadgeClass(node.role)}`}>{getRoleLabel(node.role)}</span>
        {node.status && node.status !== 'active' && <span className={`status-badge ${getStatusBadgeClass(node.status)}`}>{getStatusLabel(node.status)}</span>}
        {projectEntries.map(p => <span key={p.id} className="tree-project-badge">{p.name}: {ids[p.id]}</span>)}
      </div>
      {open && hasChildren && (
        <div className="tree-children">
          {node.children.map(c => <TreeNode key={c.id} node={c} depth={depth + 1} filtering={filtering} projects={projects} />)}
        </div>
      )}
    </div>
  );
}

export default function TreeTab({ tree, projects = [] }) {
  const [term, setTerm] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [project, setProject] = useState('');

  const filters = { term: term.trim(), role, status, project };
  const filtering = !!(filters.term || role || status || project);
  const filtered = useMemo(() => (tree ? annotate(tree, filters, projects) : null), [tree, filters.term, role, status, project, projects]);
  const hits = filtered ? countNodes(filtered) : 0;
  const reset = () => { setTerm(''); setRole(''); setStatus(''); setProject(''); };

  return (
    <div className="tab-content" data-testid="tree-content">
      <div className="section-card">
        <h3><GitBranch size={18} /> Mein User-Baum</h3>
        <div className="search-bar" style={{ marginBottom: '0.6rem' }}>
          <Search size={16} />
          <input type="text" placeholder="Suchen: Name, Benutzername, Rolle, Projekt oder Projekt-ID (z. B. NEX, 10065426)…" value={term} onChange={e => setTerm(e.target.value)} data-testid="tree-search-input" />
          {term && <button className="search-clear" onClick={() => setTerm('')} data-testid="tree-search-clear"><X size={14} /></button>}
        </div>
        <div className="tree-filters" data-testid="tree-filters">
          <Filter size={14} className="tree-filters-icon" />
          <select value={role} onChange={e => setRole(e.target.value)} className="news-mobile-cat-select tree-filter-select" data-testid="tree-filter-role">
            {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)} className="news-mobile-cat-select tree-filter-select" data-testid="tree-filter-status">
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {projects.length > 0 && (
            <select value={project} onChange={e => setProject(e.target.value)} className="news-mobile-cat-select tree-filter-select" data-testid="tree-filter-project">
              <option value="">Alle Projekte</option>
              {projects.map(p => <option key={p.id} value={p.id}>Mit {p.name}-ID</option>)}
              <option value="__none__">Ohne Projekt-ID</option>
            </select>
          )}
          {filtering && (
            <>
              <span className="tree-filter-count" data-testid="tree-filter-count">{hits} Treffer</span>
              <button type="button" className="tree-filter-reset" onClick={reset} data-testid="tree-filter-reset"><X size={12} /> Zurücksetzen</button>
            </>
          )}
        </div>
        {!tree ? (
          <div className="loading-state"><Loader2 size={24} className="spin" /><p>Lade Baumstruktur...</p></div>
        ) : filtered ? (
          <div className="tree-container" data-testid="tree-view"><TreeNode node={filtered} filtering={filtering} projects={projects} /></div>
        ) : (
          <p className="hint-text" data-testid="tree-empty">Keine Mitglieder passen zu Suche/Filter.</p>
        )}
      </div>
    </div>
  );
}
